<template>
  <div class="bg-slate-50 min-h-[calc(100vh-20rem)] py-6 relative">
    <div class="mx-auto px-4">
      <!-- Header -->
      <div class="bg-white rounded-xl shadow-sm mb-6">
        <div class="p-6 border-b border-slate-100">
          <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div class="flex items-center">
              <button @click="goBackToList" class="text-secondary mr-3">
                <span class="material-symbols-outlined">arrow_back</span>
              </button>
              <HeaderInfo :title="entityData.title" :category="entityData.category"/>
            </div>
            <ActionButtons :social-media-data="socialMediaData" :content="contentDataForActions" :can-optimize="canOptimize" :entity-url="entityUrl" :is-analyzing="isAnalyzing" :is-revised-entity-received="isRevisedEntityReceived" :is-history-content="isHistoryContent" :save-edited-content-loading="isSaving" @analyze="fetchAnalyzedContent" @toggle-history="toggleHistoryModal" @navigate="goToContent" @save-content="saveHistoryContent" @save-edited-content="saveRevision" @update:social-media-data="updateSocialMediaData"/>
          </div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <!-- Left Column -->
        <div class="lg:col-span-8 space-y-6">
          <ContentPreview :content="truncatedContent" @preview="showPreviewModal = true"/>
          <Suspense>
            <MediaGallery :image-urls="entityData.imageUrls" :is-loading="isLoading"/>
          </Suspense>
        </div>

        <!-- Right Column -->
        <div class="lg:col-span-4 space-y-6">
          <ScoreSection :score="entityData.score" :parsed-score-message="parsedScoreMessage" :carousel-items="carouselItems"/>
          <KeywordsSection :keywords="entityData.keywords"/>
          <Suspense>
            <RecommendationsSection v-if="hasRecommendations" :entity-data="recommendationData" :parsed-seo-shortcomings="parsedSeoShortcomings"/>
          </Suspense>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <PreviewContentModal v-if="showPreviewModal" v-model="showPreviewModal" :content="previewContent"/>

    <ReviseContentModal v-if="showReviseModal" v-model="showReviseModal" :selected-message="selectedMessage" :selected-type="selectedType" :selected-data="selectedData" :module-type-id="1" :entity-id="entityData.optimizeId || entityData.id" @confirm="handleReviseConfirm"/>

    <!-- Sidebar -->
    <ContentHistories ref="contentHistory" :entity-id="entityData.id" :top-id="entityData.topId" @withdrawHistory="fetchEntityDetail"/>

    <!-- Loading -->
    <div v-if="isLoading || isAnalyzing" class="absolute inset-0 bg-white bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-40">
      <Loading/>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watchEffect, defineAsyncComponent, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAssetStore } from '@/stores/asset'
import { useContentStore } from '@/stores/content'
import { entity } from '@/networking/urlManager'
import axios from '@/plugins/axiosInstance'
import { responseHandlerService } from '@/plugins/responseHandlerService'
import { safeParse } from '@/utils/common'

// Core Components
import HeaderInfo from './components/header/HeaderInfo.vue'
import ActionButtons from './components/header/ActionButtons.vue'
import ContentPreview from './components/content/ContentPreview.vue'
import ScoreSection from './components/score/ScoreSection.vue'
import KeywordsSection from './components/keyword/KeywordsSection.vue'
import Loading from '@/components/loading/VLoading.vue'

// Lazy Loaded Components
const MediaGallery = defineAsyncComponent(() =>
    import('./components/content/MediaGallery.vue'),
)
const RecommendationsSection = defineAsyncComponent(() =>
    import('./components/recommendations/RecommendationsSection.vue'),
)
const PreviewContentModal = defineAsyncComponent(() =>
    import('@/components/modals/PreviewContentModal.vue'),
)
const ReviseContentModal = defineAsyncComponent(() =>
    import('../reviseContentModal/reviseContentModal.vue'),
)
const ContentHistories = defineAsyncComponent(() =>
    import('./components/contentHistory/contentHistories.vue'),
)

// Use `defineOptions` to inherit attrs: false
defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits(['content-updated', 'back-to-list'])

// Core state management
const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const assetStore = useAssetStore()
const contentStore = useContentStore()
const contentId = computed(() => contentStore.getSelectedContentId)
const entityData = ref({})
const entityUrl = ref('')
const optimizeId = ref('')

// UI state management
const contentHistory = ref(null)
const showPreviewModal = ref(false)
const showReviseModal = ref(false)
const isLoading = ref(false)
const isAnalyzing = ref(false)
const isSaving = ref(false)
const isRevisedEntityReceived = ref(false)
const selectedMessage = ref('')
const selectedType = ref('')
const selectedData = ref('')
const isHistoryContent = ref(false)

// Back to contents list
const goBackToList = () => {
  // Store'daki hideContentDetail fonksiyonunu kullan
  contentStore.hideContentDetail(router)
  // Parent component'e bildir
  emit('back-to-list')
}

// Utility Functions
const memoizedParseJson = (jsonString, defaultValue) => {
  if (!jsonString) return defaultValue
  try {
    return JSON.parse(jsonString)
  } catch {
    return defaultValue
  }
}

const truncateText = (text, limit = 500) => {
  if (!text) return ''
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = text
  const plainText = tempDiv.textContent || tempDiv.innerText
  return plainText.length > limit ? text.substring(0, limit) + '...' : text
}

// Optimized Computed Properties
const firstImage = computed(() => {
  const images = entityData.value?.imageUrls || []
  return images[0]?.url || images[0]?.image || null
})

const contentDataForActions = computed(() => ({
  title: entityData.value.title || '',
  content: entityData.value.content || '',
  category: entityData.value.category || '',
  keywords: entityData.value.keywords || '',
  metaDescription: entityData.value.metaDescription || '',
  imageUrls: entityData.value.imageUrls || [],
  socialMediaContent: entityData.value.socialMediaContent || '',
  socialMediaHashtags: entityData.value.socialMediaHashtags || [],
}))

// Page title
const pageTitle = computed(() => t('socialMedia.components.posts.pageTitle.contentInspection'))

const socialMediaData = computed(() => ({
  entityId: entityData.value.id,
  assetId: assetStore.assetId,
  title: entityData.value.title,
  content: entityData.value?.socialMediaContent || '',
  hashtags: safeParse(entityData.value?.socialMediaHashtags, null, 'array') || [],
  url: entityUrl.value,
  image: firstImage.value,
  isSocialMediaContentReady: !!entityData.value?.socialMediaContent,
}))

const parsedScoreMessage = computed(() => {
  const message = entityData.value.scoreMessage
  const parsed = memoizedParseJson(message, null)
  if (!parsed) return { type: 'simple', message }
  return parsed.sections
      ? { type: 'detailed', data: parsed }
      : { type: 'simple', message: parsed.message || message }
})

const carouselItems = computed(() => {
  const sections = parsedScoreMessage.value?.data?.sections
  if (!sections) return []

  const items = Object.entries(sections)
      .filter(([key]) => key !== 'improvements')
      .map(([key, data]) => ({ type: 'section', key, data }))

  if (sections.improvements?.length) {
    items.push({
      type: 'improvements',
      data: sections.improvements,
    })
  }

  return items
})

const previewContent = computed(() => ({
  title: entityData.value.title || '',
  body: entityData.value.content || '',
  category: entityData.value.category || '',
  metaDescription: entityData.value.metaDescription || '',
  keywords: entityData.value.keywords?.map(k => k.name).join(', ') || '',
  images: entityData.value.imageUrls || [],
}))

const recommendationData = computed(() => ({
  semanticMessage: entityData.value.semanticMessage || '',
  spellingMessage: entityData.value.spellingMessage || '',
  seoShortcomings: entityData.value.seoShortcomings || [],
}))

const truncatedContent = computed(() =>
    truncateText(entityData.value.content),
)

const hasRecommendations = computed(() =>
    !!(entityData.value.semanticMessage ||
        entityData.value.spellingMessage ||
        entityData.value.seoShortcomings),
)

const parsedSeoShortcomings = computed(() =>
    memoizedParseJson(entityData.value.seoShortcomings, []),
)

const canOptimize = computed(() =>
    [2, 3].includes(entityData.value.entityTypeId),
)

// Helper Methods
const isValidImageUrlItem = (item) => (
    typeof item === 'object' && (item.url || item.image)
)

const isValidImageUrlArray = (array) => (
    Array.isArray(array) && array.every(isValidImageUrlItem)
)

const parseImageUrls = (imageUrlsData) => {
  if (!imageUrlsData) return []

  if (isValidImageUrlArray(imageUrlsData)) return imageUrlsData

  const parsed = safeParse(imageUrlsData, [], 'array')
  return isValidImageUrlArray(parsed) ? parsed : []
}

const updateEntityData = (data) => {
  if (!data) return

  entityUrl.value = data.entityUrl || entityUrl.value
  entityData.value = {
    ...data,
    keywords: (data.keywords || []).filter(keyword => keyword && keyword.name),
    imageUrls: parseImageUrls(data.imageUrls),
    recommendedKeywords: safeParse(data.recommendedKeywords, []),
    recomendationNotes: {
      scoreMessage: data.scoreMessage || '',
      semanticMessage: data.semanticMessage || '',
      metaDescription: data.metaDescription || '',
      seoShortcomings: data.seoShortcomings || [],
      spellingMessage: data.spellingMessage || '',
    },
  }
}

// API Methods
const validateAndPrepareRequest = (entityId, targetId) => {
  if (!contentId.value && !entityId) {
    goBackToList()
    return false
  }
  return { targetId: entityId || targetId }
}

// İstek durumunu izleyecek değişken
const isRequestInProgress = ref(false)

const checkCache = (entityId, targetId) => {
  // Eğer history içeriği ise önbellekten kontrol etme
  if (entityId) return false;
  
  // Tek seferde önbellek kontrolü ve veri alma
  const cachedContent = contentStore.getContentDetail(targetId);
  if (cachedContent) {
    if (process.env.VUE_APP_MODE === 'development') {
      console.log(`Önbellekten içerik detayı yüklendi (ID: ${targetId})`)
    }
    updateEntityData(cachedContent);
    return true;
  }
  
  return false;
}

const processResponse = (response, entityId, targetId) => {
  if (entityId) {
    optimizeId.value = entityId
    isHistoryContent.value = true
  }
  updateEntityData(response.data.data)

  if (!entityId) {
    contentStore.setContentDetail(targetId, response.data.data)
  }
}

const handleError = (error) => {
  responseHandlerService.handleError(error, t('entity.components.contentReview.errorFetch'))
  optimizeId.value = ''
  goBackToList()
}

const fetchEntityDetail = async (entityId = null) => {
  // İstek zaten devam ediyorsa yeni istek yapma
  if (isRequestInProgress.value) {
    if (process.env.VUE_APP_MODE === 'development') {
      console.log('DetailContent: Zaten bir istek devam ediyor, yeni istek engellendi')
    }
    return
  }

  const requestData = validateAndPrepareRequest(entityId, contentId.value)
  if (!requestData) return

  const { targetId } = requestData

  // Önbellek kontrolü - cache varsa kullan
  if (checkCache(entityId, targetId)) return

  isRequestInProgress.value = true
  isLoading.value = true
  
  try {
    if (process.env.VUE_APP_MODE === 'development') {
      console.log(`İçerik detayı API'den yükleniyor (ID: ${targetId})`)
    }
    
    const response = await axios.get(entity.getDetail, {
      params: {
        entityId: targetId,
        assetId: assetStore.assetId,
      },
    })

    processResponse(response, entityId, targetId)

  } catch (error) {
    handleError(error)
  } finally {
    isLoading.value = false
    isRequestInProgress.value = false
  }
}

const fetchAnalyzedContent = async () => {
  isAnalyzing.value = true
  try {
    const response = await axios.put(entity.assetContentEdit, {
      entityId: entityData.value.id,
      assetId: assetStore.assetId,
    })
    const { data } = response.data

    updateEntityData({
      ...entityData.value,
      optimizeId: data.optimizeId || '',
      title: data.result.title || '',
      score: data.result.score || '',
      content: data.result.content || '',
      entityTypeId: 3,
      keywords: data.result.keywords.filter(r => r !== '') || [],
      contentDifference: data.result.contentDifference || [],
      subTitle: data.result.spot || '',
      isActive: data.result.isActive || 1,
      recomendationNotes: {
        ...entityData.value.recomendationNotes,
        scoreMessage: data.result.scoreMessage || '',
      },
    })
    isRevisedEntityReceived.value = true
  } catch (error) {
    responseHandlerService.handleError(error, t('entity.components.contentReview.errorOptimize'))
  } finally {
    isAnalyzing.value = false
  }
}

const saveRevision = async () => {
  isSaving.value = true
  try {
    await axios.post(entity.saveEditedContent, {
      entityId: entityData.value.id,
      optimizeId: entityData.value.optimizeId,
      assetId: assetStore.assetId,
    })
    
    // İlgili önbellekleri temizle
    contentStore._invalidateRelatedCaches(entityData.value.id)
    
    await fetchEntityDetail()
    responseHandlerService.handleSuccess(t('entity.components.contentReview.contentSaved'))
    isRevisedEntityReceived.value = false
    emit('content-updated')
  } catch (error) {
    responseHandlerService.handleError(error, t('entity.components.contentReview.errorSave'))
  } finally {
    isSaving.value = false
  }
}

const handleReviseConfirm = (response) => {
  if (!response?.data) return

  const imageUrls = Array.isArray(response.data.imageUrls)
      ? response.data.imageUrls
      : typeof response.data.imageUrls === 'string'
          ? safeParse(response.data.imageUrls, [])
          : entityData.value.imageUrls

  updateEntityData({
    ...entityData.value,
    ...response.data,
    imageUrls,
    optimizeId: response.optimizeId || '',
  })
}

const saveHistoryContent = async () => {
  isLoading.value = true
  try {
    await axios.post(entity.saveEditedContent, {
      entityId: entityData.value.id,
      optimizeId: optimizeId.value,
      assetId: assetStore.assetId,
    })
    
    // İlgili önbellekleri temizle
    contentStore._invalidateRelatedCaches(entityData.value.id)
    
    await fetchEntityDetail()
    isHistoryContent.value = false
    responseHandlerService.handleSuccess(t('entity.components.contentReview.contentSaved'))
    emit('content-updated')
  } catch (error) {
    responseHandlerService.handleError(error, t('entity.components.contentReview.errorSave'))
  } finally {
    isLoading.value = false
  }
}

const goToContent = (url) => window.open(url, '_blank')
const toggleHistoryModal = () => contentHistory.value?.toggleSidebar()
const updateSocialMediaData = (newData) => {
  entityData.value = {
    ...entityData.value,
    socialMediaContent: newData.content,
    socialMediaHashtags: newData.hashtags,
  }
}

// Lifecycle
onMounted(() => {
  // Sayfa yüklenirken bir kez veri çek
  fetchEntityDetail()
})

// watchEffect KULLANMIYORUZ - onMounted yeterli
// NOT: watchEffect birden fazla tetiklenebilir ve fazladan istek yapabilir

// Sadece view parametresini ve store'daki contentId'yi izle
watch([() => contentStore.getSelectedContentId, () => route.query.view], ([newId, newView], [oldId, oldView]) => {
  // Eğer ID veya view değiştiyse şartları kontrol et
  if (newId !== oldId || newView !== oldView) {
    if (newId && newView === 'detail') {
      // Bu filtreleme sayesinde tekrarı engelleriz
      if (!entityData.value.id || entityData.value.id !== newId) {
        if (process.env.VUE_APP_MODE === 'development') {
          console.log(`ID veya view değişti, içerik yükleniyor (ID: ${newId})`)
        }
        fetchEntityDetail()
      }
    } else if (newView !== 'detail' && entityData.value.id) {
      // View değiştiyse ve içerik yüklenmisse, listeye dön
      goBackToList()
    }
  }
})

// AssetId değişimini izle
watch(() => assetStore.assetId, (newAssetId) => {
  // Eğer asset değiştiyse ve içerik detayındaysak, listeye dön
  const hasChanged = contentStore.hasAssetChanged(newAssetId)
  
  if (hasChanged && entityData.value.id) {
    if (process.env.VUE_APP_MODE === 'development') {
      console.log('ReviewContent: Asset değişti, içerik listesine dönülüyor', { 
        oldAssetId: contentStore.previousAssetId,
        newAssetId: newAssetId
      })
    }
    
    // Asset ID'yi güncelle
    contentStore.updateAssetId(newAssetId)
    
    // İçerik listesine dön
    goBackToList()
  } else {
    // Asset ID'yi güncelle
    contentStore.updateAssetId(newAssetId)
  }
})
</script>